import { get, isEmpty, isPlainObject } from 'lodash';

import { Block } from '@/components/block';
import { Button } from '@/components/button';
import { CarrierLogo } from '@/components/carrier-logo';
import { Heading } from '@/components/heading';
import { Icon } from '@/components/icon';
import { Text } from '@/components/text';
import { useTruncated } from '@/hooks/use-truncated';
import { NodeType } from '@/types/api-types';
import { BlockAlignment, BlockBody, BlockHeading, BlockId } from '@/types/block-types';
import { convertNodeTypeToMode } from '@/utils/format';
import { tv } from '@/utils/styles';

interface ColumnInterface {
  readonly key: string;
  readonly name: string;
  readonly icon?: IconInterface;
}

interface IconInterface {
  readonly component: 'carrierCode';
  readonly type: NodeType;
}

interface UrlInterface {
  readonly url: string;
  readonly label: string;
}

// Must at least have an ID
// Everything else is loose
type RowObject = {
  id: string;
} & {
  [key: string]: any | UrlInterface;
};

export interface TableBlockProps {
  id: BlockId;
  heading: BlockHeading;
  body?: BlockBody;
  blockAlignment?: BlockAlignment;
  tableHeading?: string;
  gridTemplate?: string;
  loadMore?: number;
  columns: ColumnInterface[];
  rows: RowObject[];
  noTopPadding?: boolean;
}

const TableBlock = ({
  id,
  heading,
  body,
  blockAlignment,
  gridTemplate,
  loadMore,
  columns,
  rows: rawRows,
  tableHeading,
  noTopPadding = false,
  ...props
}: TableBlockProps) => {
  const { rows, remainingRows, shouldShowLoadMore, shouldShowAllRows, showAllRows } = useTruncated(rawRows, loadMore);

  const { table, th, tr, td, thead, tbody, moreDetailsLink } = styles();

  return (
    <Block id={id} heading={heading} body={body} blockAlignment={blockAlignment} noTopPadding={noTopPadding} {...props}>
      {tableHeading && (
        <Heading variant="h4" className="mb-6 mt-4">
          {tableHeading}
        </Heading>
      )}
      <table className={table()}>
        <thead className={thead()}>
          {columns && (
            <tr>
              {columns.map((col: ColumnInterface) => (
                <th key={col.key} className={th()}>
                  {col.name}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody className={tbody()}>
          {rows &&
            rows.map((row: RowObject) => (
              <tr className={tr()} key={row.id}>
                {columns.map((col: ColumnInterface) => {
                  // This maps the Column Template provided to fields in the Rows
                  const field = get(row, col.key);
                  // If is URL, handle differently
                  if (col.key === 'link' && isPlainObject(field)) {
                    return (
                      <td key={col.key} className={td({ className: 'text-right' })}>
                        <a href={field.url} target="_blank" className={moreDetailsLink()} rel="noreferrer">
                          {field.label}
                          <Icon name="new-tab" size="base" className="ml-xs" />
                        </a>
                      </td>
                    );
                  }
                  // If we want to show a Carrier Icon
                  if (col.icon?.component === 'carrierCode') {
                    const carrierCode = get(row, 'carrierCode');
                    const segmentMode = convertNodeTypeToMode(col.icon.type);

                    return (
                      <td key={col.key} className={td()}>
                        <div className="flex items-center font-medium">
                          <CarrierLogo carrierCode={carrierCode} mode={segmentMode} className="mr-2" />
                          {field}
                        </div>
                      </td>
                    );
                  }
                  // Else return generic cell
                  return (
                    <td key={col.key} className={td()}>
                      {field}
                    </td>
                  );
                })}
              </tr>
            ))}
          {(!rows || isEmpty(rows)) && (
            <Text variant="secondary" className="my-4">
              Sorry, we don&apos;t have any data for this table
            </Text>
          )}
        </tbody>
      </table>
      {shouldShowLoadMore && (
        <Button onClick={() => showAllRows()} variant="secondaryLight" size="small">
          {shouldShowAllRows ? `Show less` : `Show all (+${remainingRows} hidden)`}
        </Button>
      )}
    </Block>
  );
};

const styles = tv({
  slots: {
    table: 'block w-full border-separate border-spacing-x-0 border-spacing-y-1 md:table',
    thead: 'hidden md:table-row-group',
    tbody: 'block w-full md:table-row-group',
    th: 'px-4 text-left text-baseSm font-normal text-grey-500 first:pl-6 last:pr-6',
    tr: 'mb-2 block rounded-lg border border-grey-300 bg-white py-4 text-baseSm leading-relaxed hover:shadow-card hover:outline-grey-400 md:mb-0 md:table-row md:rounded-none md:border-none md:py-0',
    td: 'flex px-4 md:table-cell md:border-y md:border-grey-300 md:py-4 md:first:rounded-bl-lg md:first:rounded-tl-lg md:first:border-l md:first:pl-6 md:last:rounded-br-lg md:last:rounded-tr-lg md:last:border-r md:last:pr-6',
    moreDetailsLink: 'inline-flex items-center text-text-primary hover:underline',
  },
});

export { TableBlock };
